import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Edit2, Trash2, Eye, EyeOff, Star, X, Image as ImageIcon, GripVertical } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';
import { getImageUrl } from '../../utils/imageUrl';
import './AdminPackages.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPackages = () => {
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    image: '',
    images: [],
    inclusions: [''],
    add_ons: [{ name: '', price: '' }],
    is_active: true,
    is_featured: false,
    max_guests: '',
    base_guests: '2',
    extra_person_cost: ''
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/admin/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const packageData = {
        ...formData,
        price: parseFloat(formData.price),
        max_guests: formData.max_guests ? parseInt(formData.max_guests) : null,
        base_guests: formData.base_guests ? parseInt(formData.base_guests) : 2,
        extra_person_cost: formData.extra_person_cost ? parseFloat(formData.extra_person_cost) : 0,
        inclusions: formData.inclusions.filter(item => item.trim() !== ''),
        images: formData.images.filter(url => url.trim() !== ''),
        add_ons: formData.add_ons
          .filter(addon => addon.name && addon.name.trim() !== '')
          .map(addon => ({
            name: addon.name,
            price: addon.price ? parseFloat(addon.price) : 0
          }))
      };

      if (editingPackage) {
        await axios.put(
          `${API}/admin/packages/${editingPackage.id}`,
          packageData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/admin/packages`,
          packageData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      fetchPackages();
      closeModal();
      alert(editingPackage ? 'Package updated!' : 'Package created!');
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Failed to save package');
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      await axios.delete(`${API}/admin/packages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPackages();
      alert('Package deleted!');
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Failed to delete package');
    }
  };

  const toggleActive = async (pkg) => {
    try {
      await axios.put(
        `${API}/admin/packages/${pkg.id}`,
        { is_active: !pkg.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPackages();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const toggleFeatured = async (pkg) => {
    try {
      await axios.put(
        `${API}/admin/packages/${pkg.id}`,
        { is_featured: !pkg.is_featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPackages();
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const openModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description,
        price: pkg.price.toString(),
        currency: pkg.currency,
        image: pkg.image,
        images: pkg.images || [],
        inclusions: pkg.inclusions.length > 0 ? pkg.inclusions : [''],
        add_ons: pkg.add_ons && pkg.add_ons.length > 0 
          ? pkg.add_ons.map(addon => {
              if (typeof addon === 'string') {
                return { name: addon, price: '' };
              }
              return { name: addon.name, price: addon.price ? addon.price.toString() : '' };
            })
          : [{ name: '', price: '' }],
        is_active: pkg.is_active,
        is_featured: pkg.is_featured,
        max_guests: pkg.max_guests ? pkg.max_guests.toString() : '',
        base_guests: pkg.base_guests ? pkg.base_guests.toString() : '2',
        extra_person_cost: pkg.extra_person_cost ? pkg.extra_person_cost.toString() : ''
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        currency: 'USD',
        image: '',
        images: [],
        inclusions: [''],
        add_ons: [{ name: '', price: '' }],
        is_active: true,
        is_featured: false,
        max_guests: '',
        base_guests: '2',
        extra_person_cost: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPackage(null);
  };

  const handleArrayChange = (index, value, field) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const handleAddonChange = (index, field, value) => {
    const newAddons = [...formData.add_ons];
    newAddons[index][field] = value;
    setFormData({ ...formData, add_ons: newAddons });
  };

  const addArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const addAddonItem = () => {
    setFormData({ ...formData, add_ons: [...formData.add_ons, { name: '', price: '' }] });
  };

  const removeArrayItem = (index, field) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  const removeAddonItem = (index) => {
    const newAddons = formData.add_ons.filter((_, i) => i !== index);
    setFormData({ ...formData, add_ons: newAddons });
  };

  if (loading) {
    return <div className="loading">Loading packages...</div>;
  }

  return (
    <div className="admin-packages">
      <div className="packages-header">
        <div>
          <h2>Package Management</h2>
          <p>Manage your service packages and pricing</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus size={18} /> Add Package
        </button>
      </div>

      <div className="packages-grid">
        {packages.map((pkg) => (
          <div key={pkg.id} className="package-card">
            <div className="package-image">
              <img src={getImageUrl(pkg.image)} alt={pkg.name} />
              <div className="package-badges">
                {pkg.is_featured && (
                  <span className="badge-featured">
                    <Star size={12} /> Featured
                  </span>
                )}
                {!pkg.is_active && (
                  <span className="badge-inactive">Inactive</span>
                )}
              </div>
              {pkg.images && pkg.images.length > 0 && (
                <span className="badge-photo-count" data-testid="photo-count-badge">
                  <ImageIcon size={12} /> {1 + pkg.images.length} photos
                </span>
              )}
            </div>
            
            <div className="package-content">
              <h3>{pkg.name}</h3>
              <p className="package-price">${pkg.price.toFixed(2)} {pkg.currency}</p>
              <p className="package-description">{pkg.description}</p>
              
              <div className="package-meta">
                <span>✓ {pkg.inclusions.length} inclusions</span>
                <span>+ {pkg.add_ons.length} add-ons</span>
              </div>
              
              <div className="package-actions">
                <button onClick={() => toggleActive(pkg)} className="btn-icon" title={pkg.is_active ? 'Deactivate' : 'Activate'}>
                  {pkg.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => toggleFeatured(pkg)} className="btn-icon" title={pkg.is_featured ? 'Unfeature' : 'Feature'}>
                  <Star size={16} fill={pkg.is_featured ? 'currentColor' : 'none'} />
                </button>
                <button onClick={() => openModal(pkg)} className="btn-icon" title="Edit">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => deletePackage(pkg.id)} className="btn-icon btn-danger" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {packages.length === 0 && (
        <div className="empty-state">
          <p>No packages yet. Create your first package!</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPackage ? 'Edit Package' : 'Create Package'}</h3>
              <button onClick={closeModal} className="modal-close">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Package Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Cover Image *</label>
                <ImageUpload
                  currentImageUrl={formData.image}
                  onUploadSuccess={(url) => setFormData({ ...formData, image: url })}
                />
                <small style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Or enter image URL manually:
                </small>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://... or /api/uploads/..."
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div className="form-group">
                <label>Additional Photos</label>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Add more photos to showcase this package. These will appear in a gallery on the website.
                </p>
                {formData.images.length > 0 && (
                  <div className="additional-images-grid" data-testid="additional-images-grid">
                    {formData.images.map((imgUrl, index) => (
                      <div key={index} className="additional-image-item" data-testid={`additional-image-${index}`}>
                        <img src={imgUrl} alt={`Photo ${index + 1}`} />
                        <button
                          type="button"
                          className="remove-additional-image"
                          onClick={() => {
                            const newImages = formData.images.filter((_, i) => i !== index);
                            setFormData({ ...formData, images: newImages });
                          }}
                          data-testid={`remove-additional-image-${index}`}
                        >
                          <X size={14} />
                        </button>
                        <span className="image-order-label">{index + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="add-photo-section">
                  <ImageUpload
                    currentImageUrl=""
                    resetAfterUpload={true}
                    onUploadSuccess={(url) => {
                      if (url) {
                        setFormData(prev => ({ ...prev, images: [...prev.images, url] }));
                      }
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Max Guests</label>
                <input
                  type="number"
                  value={formData.max_guests}
                  onChange={(e) => setFormData({ ...formData, max_guests: e.target.value })}
                  placeholder="Optional"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Base Guests Included</label>
                  <input
                    type="number"
                    value={formData.base_guests}
                    onChange={(e) => setFormData({ ...formData, base_guests: e.target.value })}
                    placeholder="2"
                    min="1"
                  />
                  <small style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Number of guests included in base price
                  </small>
                </div>
                
                <div className="form-group">
                  <label>Extra Person Cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.extra_person_cost}
                    onChange={(e) => setFormData({ ...formData, extra_person_cost: e.target.value })}
                    placeholder="0.00"
                  />
                  <small style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Cost per extra person beyond base guests
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>Inclusions</label>
                {formData.inclusions.map((item, index) => (
                  <div key={index} className="array-input">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayChange(index, e.target.value, 'inclusions')}
                      placeholder="What's included..."
                    />
                    {formData.inclusions.length > 1 && (
                      <button type="button" onClick={() => removeArrayItem(index, 'inclusions')} className="btn-remove">
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addArrayItem('inclusions')} className="btn-add-item">
                  + Add Inclusion
                </button>
              </div>

              <div className="form-group">
                <label>Add-ons (with pricing)</label>
                {formData.add_ons.map((addon, index) => (
                  <div key={index} className="addon-input-group">
                    <input
                      type="text"
                      value={addon.name}
                      onChange={(e) => handleAddonChange(index, 'name', e.target.value)}
                      placeholder="Add-on name..."
                      className="addon-name-input"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={addon.price}
                      onChange={(e) => handleAddonChange(index, 'price', e.target.value)}
                      placeholder="Price"
                      className="addon-price-input"
                    />
                    {formData.add_ons.length > 1 && (
                      <button type="button" onClick={() => removeAddonItem(index)} className="btn-remove">
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addAddonItem} className="btn-add-item">
                  + Add Add-on
                </button>
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Active (visible on website)</span>
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  />
                  <span>Featured Package</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingPackage ? 'Update Package' : 'Create Package'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
